/**
 * Market module — redesigned (iter 10) with sub-tabs.
 *
 * Sub-tabs:
 *  01 Visión general — market size (TAM/SAM/SOM), opportunity, ideal client.
 *  02 Análisis SWOT — 2×2 matrix.
 *  03 Competencia — per-competitor cards + DEEP analysis section with
 *     comparison matrix, key insights and strategic conclusions.
 *  04 Buyer Personas — multi-persona cards with templates.
 *
 * Adds the new `competitor_insights` field (synthetic conclusions / strategic
 * narrative) which is saved alongside the existing competitor list. Backward
 * compatible: old saves without `competitor_insights` simply show an empty
 * field.
 */
import { useEffect, useState } from "react";
import {
  Plus,
  Trash2,
  Sparkles,
  Eye,
  Layers,
  Users as UsersIcon,
  Target,
} from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import {
  SectionCard,
  FieldLabel,
  TextInput,
  TextArea,
  PrimaryButton,
  SecondaryButton,
  AIBadge,
  Overline,
  SubTabs,
} from "@/components/ui-kit";
import AIAssistant from "@/components/AIAssistant";
import { PERSONA_TEMPLATES } from "@/i18n/translations";

const empty = {
  market_size: "",
  opportunity: "",
  competitors: [],
  competitor_insights: "",
  client_definition: "",
  buyer_personas: [],
  swot: { strengths: "", weaknesses: "", opportunities: "", threats: "" },
};

export default function MarketModule({ data, project, onSave }) {
  const { t, lang } = useLang();
  const [v, setV] = useState({ ...empty, ...(data || {}) });
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    setV((prev) => {
      const next = {
        ...empty,
        ...(data || {}),
        swot: { ...empty.swot, ...((data && data.swot) || {}) },
      };
      try { if (JSON.stringify(prev) === JSON.stringify(next)) return prev; } catch (e) { void e; }
      return next;
    });
  }, [data]);
  useEffect(() => {
    onSave?.(v);
  }, [v, onSave]);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const setSwot = (k, val) => setV((p) => ({ ...p, swot: { ...p.swot, [k]: val } }));

  const addCompetitor = () =>
    setV((p) => ({
      ...p,
      competitors: [...(p.competitors || []), { name: "", description: "", strengths: "", weaknesses: "", positioning: "", price_range: "" }],
    }));
  const updateCompetitor = (i, k, val) =>
    setV((p) => ({ ...p, competitors: p.competitors.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const removeCompetitor = (i) =>
    setV((p) => ({ ...p, competitors: p.competitors.filter((_, idx) => idx !== i) }));

  const addPersona = (template = null) => {
    const base = template
      ? PERSONA_TEMPLATES[template]
      : { name: "", age: "", role: "", goals: "", pains: "", channels: "" };
    setV((p) => ({ ...p, buyer_personas: [...(p.buyer_personas || []), { ...base }] }));
  };
  const updatePersona = (i, k, val) =>
    setV((p) => ({ ...p, buyer_personas: p.buyer_personas.map((x, idx) => (idx === i ? { ...x, [k]: val } : x)) }));
  const removePersona = (i) =>
    setV((p) => ({ ...p, buyer_personas: p.buyer_personas.filter((_, idx) => idx !== i) }));

  const ctx = { project: project?.name, market_size: v.market_size, client_definition: v.client_definition };
  const suggestions = lang === "es"
    ? [
        "Estima TAM, SAM y SOM para este negocio en EUR",
        "Lista 5 competidores directos con sus fortalezas y debilidades",
        "Redacta conclusiones del análisis de competencia: posicionamiento, gaps, oportunidad",
        "Crea un buyer persona detallado para este negocio",
        "Redacta un análisis SWOT completo",
      ]
    : [
        "Estimate TAM, SAM and SOM in EUR",
        "List 5 direct competitors with strengths and weaknesses",
        "Write competition analysis conclusions: positioning, gaps, opportunity",
        "Create a detailed buyer persona",
        "Draft a complete SWOT analysis",
      ];

  const tabs = [
    { key: "overview",    label: lang === "es" ? "Visión general" : "Overview",    icon: Eye },
    { key: "swot",        label: lang === "es" ? "Análisis SWOT"   : "SWOT analysis", icon: Layers },
    { key: "competition", label: lang === "es" ? "Competencia"     : "Competition", icon: Target },
    { key: "personas",    label: lang === "es" ? "Buyer Personas"  : "Buyer Personas", icon: UsersIcon },
  ];

  return (
    <>
      <SectionCard
        title={t("module.market")}
        subtitle={lang === "es" ? "Tamaño, oportunidad, competencia y arquetipos de cliente." : "Size, opportunity, competition and customer archetypes."}
        action={
          <div className="flex items-center gap-3">
            <AIBadge />
            <AIAssistant module="market" context={ctx} suggestions={suggestions} />
          </div>
        }
        testId="market-section"
      >
        <SubTabs tabs={tabs} active={tab} onChange={setTab} testIdPrefix="market-subtab" />

        {/* TAB 01: OVERVIEW */}
        {tab === "overview" && (
          <div className="space-y-6" data-testid="market-tab-overview">
            <div>
              <FieldLabel hint={lang === "es" ? "TAM · SAM · SOM con cifras y fuentes" : "TAM · SAM · SOM with figures and sources"}>{t("market.size")}</FieldLabel>
              <TextArea value={v.market_size} onChange={(x) => set("market_size", x)} rows={14} testId="market-size-textarea" />
            </div>
            <div>
              <FieldLabel hint={lang === "es" ? "Demografía · drivers económicos · marco regulatorio · gap competitivo · por qué AHORA" : "Demographics · economic drivers · regulatory · competitive gap · why NOW"}>{t("market.opportunity")}</FieldLabel>
              <TextArea value={v.opportunity} onChange={(x) => set("opportunity", x)} rows={20} testId="market-opportunity-textarea" />
            </div>
            <div>
              <FieldLabel hint={lang === "es" ? "Defínelo en 1 frase" : "In one sentence"}>{t("market.client_def")}</FieldLabel>
              <TextArea value={v.client_definition} onChange={(x) => set("client_definition", x)} rows={3} testId="market-client-textarea" />
            </div>
          </div>
        )}

        {/* TAB 02: SWOT */}
        {tab === "swot" && (
          <div data-testid="market-tab-swot">
            <p className="text-sm text-[#5C5B5A] mb-5">
              {lang === "es"
                ? "Fortalezas y debilidades son internas. Oportunidades y amenazas son externas."
                : "Strengths and weaknesses are internal. Opportunities and threats are external."}
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-[#E8E6E1] rounded-sm overflow-hidden">
              <div className="p-6 border-r border-b border-[#E8E6E1] bg-[#EFF4FF]/40">
                <div className="text-xs uppercase tracking-[0.2em] text-[#2563EB] font-medium mb-3">
                  {t("swot.strengths")} <span className="text-[#8A8886]">/ S</span>
                </div>
                <TextArea value={v.swot.strengths} onChange={(x) => setSwot("strengths", x)} rows={6} testId="swot-strengths-textarea" />
              </div>
              <div className="p-6 border-b border-[#E8E6E1] bg-[#FAEDED]/40">
                <div className="text-xs uppercase tracking-[0.2em] text-[#9E2A2B] font-medium mb-3">
                  {t("swot.weaknesses")} <span className="text-[#8A8886]">/ W</span>
                </div>
                <TextArea value={v.swot.weaknesses} onChange={(x) => setSwot("weaknesses", x)} rows={6} testId="swot-weaknesses-textarea" />
              </div>
              <div className="p-6 border-r border-[#E8E6E1] bg-[#F5EFE6]/50">
                <div className="text-xs uppercase tracking-[0.2em] text-[#B89154] font-medium mb-3">
                  {t("swot.opportunities")} <span className="text-[#8A8886]">/ O</span>
                </div>
                <TextArea value={v.swot.opportunities} onChange={(x) => setSwot("opportunities", x)} rows={6} testId="swot-opportunities-textarea" />
              </div>
              <div className="p-6 bg-[#F1EBE3]/60">
                <div className="text-xs uppercase tracking-[0.2em] text-[#3A5A6B] font-medium mb-3">
                  {t("swot.threats")} <span className="text-[#8A8886]">/ T</span>
                </div>
                <TextArea value={v.swot.threats} onChange={(x) => setSwot("threats", x)} rows={6} testId="swot-threats-textarea" />
              </div>
            </div>
          </div>
        )}

        {/* TAB 03: COMPETITION */}
        {tab === "competition" && (
          <div data-testid="market-tab-competition" className="space-y-8">
            <div className="flex items-center justify-between gap-4">
              <div>
                <Overline>{lang === "es" ? "Mapa competitivo" : "Competitive map"}</Overline>
                <p className="text-sm text-[#5C5B5A] mt-2 max-w-2xl">
                  {lang === "es"
                    ? "Lista 4-6 competidores directos. Para cada uno: descripción, fortalezas, debilidades, posicionamiento y rango de precios. Cierra con CONCLUSIONES estratégicas."
                    : "List 4-6 direct competitors. For each: description, strengths, weaknesses, positioning and price range. Close with strategic CONCLUSIONS."}
                </p>
              </div>
              <SecondaryButton onClick={addCompetitor} testId="add-competitor-btn">
                <Plus className="h-4 w-4" strokeWidth={1.5} />
                {t("market.add_competitor")}
              </SecondaryButton>
            </div>

            {/* Competitor cards */}
            {(v.competitors || []).length === 0 ? (
              <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                {lang === "es" ? "Sin competidores. Añade el primero." : "No competitors yet. Add the first one."}
              </div>
            ) : (
              <div className="space-y-4">
                {v.competitors.map((c, i) => (
                  <div key={i} className="border border-[#E8E6E1] rounded-sm p-6 bg-white" data-testid={`competitor-row-${i}`}>
                    <div className="flex items-baseline gap-3 mb-4">
                      <span
                        className="font-mono text-xs text-[#8A8886] shrink-0"
                        style={{ fontFamily: '"IBM Plex Mono", monospace' }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </span>
                      <TextInput
                        value={c.name}
                        onChange={(x) => updateCompetitor(i, "name", x)}
                        placeholder={lang === "es" ? "Nombre del competidor" : "Competitor name"}
                        testId={`competitor-${i}-name`}
                      />
                    </div>
                    <div className="grid md:grid-cols-2 gap-4">
                      <div className="md:col-span-2">
                        <FieldLabel>{lang === "es" ? "Descripción" : "Description"}</FieldLabel>
                        <TextArea value={c.description} onChange={(x) => updateCompetitor(i, "description", x)} rows={2} testId={`competitor-${i}-desc`} />
                      </div>
                      <div>
                        <FieldLabel>
                          <span className="text-[#2563EB]">{lang === "es" ? "Fortalezas" : "Strengths"}</span>
                        </FieldLabel>
                        <TextArea value={c.strengths} onChange={(x) => updateCompetitor(i, "strengths", x)} rows={3} testId={`competitor-${i}-strengths`} />
                      </div>
                      <div>
                        <FieldLabel>
                          <span className="text-[#9E2A2B]">{lang === "es" ? "Debilidades" : "Weaknesses"}</span>
                        </FieldLabel>
                        <TextArea value={c.weaknesses} onChange={(x) => updateCompetitor(i, "weaknesses", x)} rows={3} testId={`competitor-${i}-weaknesses`} />
                      </div>
                      <div>
                        <FieldLabel>{lang === "es" ? "Posicionamiento" : "Positioning"}</FieldLabel>
                        <TextInput
                          value={c.positioning || ""}
                          onChange={(x) => updateCompetitor(i, "positioning", x)}
                          placeholder={lang === "es" ? "Ej: Premium · low-cost · nicho geográfico" : "e.g. Premium · low-cost · geo-niche"}
                          testId={`competitor-${i}-positioning`}
                        />
                      </div>
                      <div>
                        <FieldLabel>{lang === "es" ? "Rango de precios" : "Price range"}</FieldLabel>
                        <TextInput
                          value={c.price_range || ""}
                          onChange={(x) => updateCompetitor(i, "price_range", x)}
                          placeholder="€2.000 – €5.000"
                          testId={`competitor-${i}-price`}
                        />
                      </div>
                    </div>
                    <div className="mt-4 flex justify-end">
                      <button onClick={() => removeCompetitor(i)} className="text-[#8A8886] hover:text-[#9E2A2B] text-sm inline-flex items-center gap-1" data-testid={`competitor-${i}-remove`}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} /> {t("common.delete")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Comparison matrix (read-only computed view) */}
            {(v.competitors || []).length >= 2 && (
              <div className="border border-[#E8E6E1] rounded-sm bg-white p-6">
                <Overline>{lang === "es" ? "Matriz comparativa" : "Comparison matrix"}</Overline>
                <div className="mt-4 overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="border-b-2 border-[#1A1A1A] text-left">
                      <tr>
                        <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A]">{lang === "es" ? "Competidor" : "Competitor"}</th>
                        <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A]">{lang === "es" ? "Posicionamiento" : "Positioning"}</th>
                        <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#5C5B5A]">{lang === "es" ? "Precios" : "Price"}</th>
                        <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#2563EB]">{lang === "es" ? "Fortalezas" : "Strengths"}</th>
                        <th className="px-3 py-2.5 text-[10px] uppercase tracking-wide text-[#9E2A2B]">{lang === "es" ? "Debilidades" : "Weaknesses"}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {v.competitors.map((c, i) => (
                        <tr key={i} className="border-b border-[#F0EFE9]">
                          <td className="px-3 py-2.5 font-medium text-[#1A1A1A]">{c.name || "—"}</td>
                          <td className="px-3 py-2.5 text-[#5C5B5A]">{c.positioning || "—"}</td>
                          <td className="px-3 py-2.5 font-mono text-xs text-[#1A1A1A]" style={{ fontFamily: '"IBM Plex Mono", monospace' }}>{c.price_range || "—"}</td>
                          <td className="px-3 py-2.5 text-xs text-[#5C5B5A]">{c.strengths || "—"}</td>
                          <td className="px-3 py-2.5 text-xs text-[#5C5B5A]">{c.weaknesses || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Strategic conclusions */}
            <div className="border-l-4 border-[#B89154] bg-[#F5EFE6]/50 rounded-sm p-6">
              <Overline><span className="text-[#B89154]">{lang === "es" ? "Conclusiones estratégicas" : "Strategic conclusions"}</span></Overline>
              <p className="text-xs text-[#8A8886] mt-1 mb-3">
                {lang === "es"
                  ? "Síntesis del análisis: ¿dónde están los gaps?, ¿qué cuadrante del mercado está desatendido?, ¿cuál es nuestra ventaja vs cada competidor?, ¿qué amenazas vigilar?"
                  : "Synthesis: where are the gaps? Which market quadrant is underserved? What is our edge vs each competitor? What threats to monitor?"}
              </p>
              <TextArea
                value={v.competitor_insights}
                onChange={(x) => set("competitor_insights", x)}
                rows={8}
                placeholder={lang === "es"
                  ? "Ej: 1. Ningún competidor cubre los 3 destinos con un equipo bilingüe. 2. Los players locales tienen precios menores pero baja calidad de servicio. 3. Nuestra ventaja es la integración fiscal US-EU. 4. Vigilar: nuevos players de DC o NY con expansión internacional."
                  : "e.g. 1. No competitor covers all 3 destinations bilingually. 2. Local players are cheaper but lower service. 3. Our edge: US-EU tax integration. 4. Watch: new DC/NY players going international."}
                testId="competitor-insights-textarea"
              />
            </div>
          </div>
        )}

        {/* TAB 04: PERSONAS */}
        {tab === "personas" && (
          <div data-testid="market-tab-personas">
            <div className="flex items-center justify-between gap-4 mb-5">
              <p className="text-sm text-[#5C5B5A] max-w-2xl">
                {lang === "es"
                  ? "Crea 2-3 buyer personas distintos. Cada uno representa un arquetipo de cliente con objetivos, dolores y canales propios."
                  : "Create 2-3 distinct buyer personas. Each represents a customer archetype with their own goals, pains and channels."}
              </p>
              <div className="flex items-center gap-2 shrink-0">
                <div className="relative group">
                  <SecondaryButton testId="persona-template-btn">
                    <Sparkles className="h-4 w-4" strokeWidth={1.5} />
                    {t("market.use_template")}
                  </SecondaryButton>
                  <div className="absolute right-0 top-full mt-1 hidden group-hover:block bg-white border border-[#E8E6E1] rounded-sm shadow-sm z-10 min-w-[220px]">
                    {Object.entries(PERSONA_TEMPLATES).map(([k, p]) => (
                      <button
                        key={k}
                        onClick={() => addPersona(k)}
                        className="block w-full text-left px-4 py-2 text-sm hover:bg-[#F5EFE6] transition-colors"
                        data-testid={`persona-template-${k}`}
                      >
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>
                <SecondaryButton onClick={() => addPersona()} testId="add-persona-btn">
                  <Plus className="h-4 w-4" strokeWidth={1.5} />
                  {t("market.add_persona")}
                </SecondaryButton>
              </div>
            </div>
            {(v.buyer_personas || []).length === 0 ? (
              <div className="text-sm text-[#8A8886] py-6 text-center border border-dashed border-[#E8E6E1] rounded-sm">
                {lang === "es" ? "Sin buyer personas. Añade el primero." : "No buyer personas. Add the first one."}
              </div>
            ) : (
              <div className="grid lg:grid-cols-2 gap-5">
                {v.buyer_personas.map((p, i) => (
                  <div key={i} className="border border-[#E8E6E1] rounded-sm p-6 space-y-4 bg-white" data-testid={`persona-${i}`}>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <FieldLabel>{lang === "es" ? "Nombre" : "Name"}</FieldLabel>
                        <TextInput value={p.name} onChange={(x) => updatePersona(i, "name", x)} testId={`persona-${i}-name`} />
                      </div>
                      <div>
                        <FieldLabel>{lang === "es" ? "Edad" : "Age"}</FieldLabel>
                        <TextInput value={p.age} onChange={(x) => updatePersona(i, "age", x)} testId={`persona-${i}-age`} />
                      </div>
                    </div>
                    <div>
                      <FieldLabel>{lang === "es" ? "Rol / Profesión" : "Role / Job"}</FieldLabel>
                      <TextInput value={p.role} onChange={(x) => updatePersona(i, "role", x)} testId={`persona-${i}-role`} />
                    </div>
                    <div>
                      <FieldLabel>{lang === "es" ? "Objetivos" : "Goals"}</FieldLabel>
                      <TextArea value={p.goals} onChange={(x) => updatePersona(i, "goals", x)} rows={3} testId={`persona-${i}-goals`} />
                    </div>
                    <div>
                      <FieldLabel>{lang === "es" ? "Dolores" : "Pains"}</FieldLabel>
                      <TextArea value={p.pains} onChange={(x) => updatePersona(i, "pains", x)} rows={3} testId={`persona-${i}-pains`} />
                    </div>
                    <div>
                      <FieldLabel>{lang === "es" ? "Canales (dónde está)" : "Channels (where they hang out)"}</FieldLabel>
                      <TextArea value={p.channels} onChange={(x) => updatePersona(i, "channels", x)} rows={2} testId={`persona-${i}-channels`} />
                    </div>
                    <div className="flex justify-end">
                      <button onClick={() => removePersona(i)} className="text-[#8A8886] hover:text-[#9E2A2B] text-sm inline-flex items-center gap-1" data-testid={`persona-${i}-remove`}>
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} /> {t("common.delete")}
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </SectionCard>

    </>
  );
}
